import type { Graphics } from 'pixi.js';
import { MASK_BODY, maskTone } from '../face-kit';
import type { Frame } from '../frame';

export interface Point {
  readonly x: number;
  readonly y: number;
}

const WIDTH = { rim: 0.2, core: 0.11 };
const KNOT = 0.16;

export interface Bend {
  readonly drop: number;
  readonly sag: number;
  readonly knot?: number;
}

function controls(foot: Point, plug: Point, radius: number, bend: Bend): [Point, Point] {
  const dx = plug.x - foot.x;
  const dy = plug.y - foot.y;
  const length = Math.hypot(dx, dy) || 1;
  return [
    { x: foot.x, y: foot.y + bend.drop * radius },
    {
      x: plug.x - 0.3 * dx - (dy / length) * bend.sag * radius,
      y: plug.y - 0.3 * dy + (dx / length) * bend.sag * radius,
    },
  ];
}

export function cablePoint(
  foot: Point,
  plug: Point,
  radius: number,
  bend: Bend,
  share: number,
): Point {
  const [c1, c2] = controls(foot, plug, radius, bend);
  return along(foot, c1, c2, plug, share);
}

export function drawCable(
  floor: Graphics,
  foot: Point,
  plug: Point,
  radius: number,
  bend: Bend,
  alpha: number,
  frame: Frame,
): void {
  const [c1, c2] = controls(foot, plug, radius, bend);
  const rim = frame.palette.badVibeRim;
  const strands: readonly (readonly [number, number])[] = [
    [WIDTH.rim, rim],
    [WIDTH.core, maskTone(rim, MASK_BODY)],
  ];
  const knot = bend.knot === undefined ? undefined : along(foot, c1, c2, plug, bend.knot);
  for (const [width, color] of strands) {
    floor
      .moveTo(foot.x, foot.y)
      .bezierCurveTo(c1.x, c1.y, c2.x, c2.y, plug.x, plug.y)
      .stroke({ width: width * radius, color, alpha, cap: 'round' });
    if (knot !== undefined) {
      floor
        .circle(knot.x + KNOT * radius, knot.y, KNOT * radius)
        .stroke({ width: width * radius, color, alpha });
    }
  }
}

function along(a: Point, b: Point, c: Point, d: Point, t: number): Point {
  const u = 1 - t;
  const [wa, wb, wc, wd] = [u * u * u, 3 * u * u * t, 3 * u * t * t, t * t * t];
  return {
    x: wa * a.x + wb * b.x + wc * c.x + wd * d.x,
    y: wa * a.y + wb * b.y + wc * c.y + wd * d.y,
  };
}
